/**
 * Package Feature Constants
 * All hard-coded values, configuration options, and enums for the packages feature
 */

// ============================================================================
// PACKAGE STATUS OPTIONS
// ============================================================================

/**
 * Package status options for filtering and display
 * Use translation keys for labels: t(`status.${value}`)
 */
export const PACKAGE_STATUS_OPTIONS = [
  { value: "pending", label: "status.pending" },
  { value: "confirmed", label: "status.order_confirmed" },
  { value: "out_for_delivery", label: "status.out_for_delivery" },
  { value: "signed", label: "status.signed" },
  { value: "cancelled", label: "status.cancelled" },
];

/**
 * Enhanced livreur status options with workflow progression
 * Labels should be translated: t(`packages.livreurStatus.${value}.label`)
 * Descriptions should be translated: t(`packages.livreurStatus.${value}.description`)
 */
export const ENHANCED_LIVREUR_STATUS_OPTIONS = [
  {
    value: "to_pick_up",
    label: "packages.livreurStatus.to_pick_up.label",
    description: "packages.livreurStatus.to_pick_up.description",
    color: "bg-purple-500",
    iconName: "Package",
    order: 1,
    nextSteps: ["picked_up"],
  },
  {
    value: "picked_up",
    label: "packages.livreurStatus.picked_up.label",
    description: "packages.livreurStatus.picked_up.description",
    color: "bg-blue-500",
    iconName: "Package",
    order: 2,
    nextSteps: ["in_transit"],
  },
  {
    value: "in_transit",
    label: "packages.livreurStatus.in_transit.label",
    description: "packages.livreurStatus.in_transit.description",
    color: "bg-yellow-500",
    iconName: "Truck",
    order: 3,
    nextSteps: ["arrived_at_warehouse", "to_deliver"],
  },
  {
    value: "arrived_at_warehouse",
    label: "packages.livreurStatus.arrived_at_warehouse.label",
    description: "packages.livreurStatus.arrived_at_warehouse.description",
    color: "bg-cyan-500",
    iconName: "Navigation",
    order: 4,
    nextSteps: ["warehouse_processing"],
  },
  {
    value: "warehouse_processing",
    label: "packages.livreurStatus.warehouse_processing.label",
    description: "packages.livreurStatus.warehouse_processing.description",
    color: "bg-teal-500",
    iconName: "Clock",
    order: 5,
    nextSteps: ["to_deliver", "self_pickup_putaway"],
  },
  {
    value: "to_deliver",
    label: "packages.livreurStatus.to_deliver.label",
    description: "packages.livreurStatus.to_deliver.description",
    color: "bg-lime-500",
    iconName: "Package",
    order: 6,
    nextSteps: ["delivering"],
  },
  {
    value: "self_pickup_putaway",
    label: "packages.livreurStatus.self_pickup_putaway.label",
    description: "packages.livreurStatus.self_pickup_putaway.description",
    color: "bg-emerald-500",
    iconName: "MapPin",
    order: 6,
    nextSteps: ["signed"],
  },
  {
    value: "delivering",
    label: "packages.livreurStatus.delivering.label",
    description: "packages.livreurStatus.delivering.description",
    color: "bg-orange-500",
    iconName: "Truck",
    order: 7,
    nextSteps: ["signed", "abnormal_signed", "return_collection"],
  },
  {
    value: "signed",
    label: "packages.livreurStatus.signed.label",
    description: "packages.livreurStatus.signed.description",
    color: "bg-green-500",
    iconName: "CheckCircle",
    order: 8,
    nextSteps: [],
  },
  {
    value: "abnormal_signed",
    label: "packages.livreurStatus.abnormal_signed.label",
    description: "packages.livreurStatus.abnormal_signed.description",
    color: "bg-amber-500",
    iconName: "AlertCircle",
    order: 8,
    nextSteps: [],
  },
  {
    value: "return_collection",
    label: "packages.livreurStatus.return_collection.label",
    description: "packages.livreurStatus.return_collection.description",
    color: "bg-red-500",
    iconName: "XCircle",
    order: 8,
    nextSteps: [],
  },
];

// ============================================================================
// QUICK ACTION TEMPLATES
// ============================================================================

/**
 * Quick action templates for common delivery scenarios
 * Notes should be translated: t(`packages.quickActions.${key}.note`)
 */
export const QUICK_ACTIONS = {
  successful_delivery: {
    status: "signed",
    noteKey: "packages.quickActions.successful_delivery.note",
  },
  customer_not_home: {
    status: "abnormal_signed",
    noteKey: "packages.quickActions.customer_not_home.note",
  },
  wrong_address: {
    status: "abnormal_signed",
    noteKey: "packages.quickActions.wrong_address.note",
  },
  customer_refused: {
    status: "return_collection",
    noteKey: "packages.quickActions.customer_refused.note",
  },
  return_to_warehouse: {
    status: "return_collection",
    noteKey: "packages.quickActions.return_to_warehouse.note",
  },
};

// ============================================================================
// FILTER CONFIGURATION
// ============================================================================

/**
 * Package filter configuration for BaseFilter component
 */
export const PACKAGE_FILTER_CONFIG = {
  showPhoneFilter: true,
  showCityFilter: true,
  showUserFilter: true,
  showDateRangeFilter: true,
  phoneFieldName: "customer.phone_q",
  cityFieldName: "customer.city",
  phonePlaceholder: "0600000000",
  entityName: "packages",
  maxTabCols: 6,
};

// ============================================================================
// FORM FIELD NAMES
// ============================================================================

/**
 * Package form field names (used in forms and validation)
 */
export const PACKAGE_FORM_FIELDS = {
  CUSTOMER_NAME: "customer.name",
  CUSTOMER_PHONE: "customer.phone",
  CUSTOMER_CITY: "customer.city",
  CUSTOMER_ADDRESS: "customer.address",
  PRODUCT_NAME: "productName",
  QUANTITY: "quantity",
  PRICE: "price",
  TOTAL_AMOUNT: "totalAmount",
  NOTE: "note",
  STATUS: "status",
  TRACKING_NUMBER: "trackingNumber",
  REFERENCE: "reference",
};

// ============================================================================
// TOAST MESSAGE KEYS
// ============================================================================

/**
 * Toast message translation keys for package operations
 */
export const PACKAGE_TOAST_KEYS = {
  CREATING: "toast.creatingPackage",
  CREATED_SUCCESS: "toast.packageCreated",
  UPDATE_SUCCESS: "toast.packageUpdated",
  DELETE_SUCCESS: "toast.packageDeleted",
  STATUS_UPDATE_SUCCESS: "packages.statusUpdateSuccess",
  STATUS_UPDATE_FAILED: "packages.statusUpdateFailed",
  INVALID_TRACKING: "packages.trackingError",
  NOT_FOUND: "packages.packageNotFound",
};

// ============================================================================
// ROUTE PATHS
// ============================================================================

/**
 * Package-related route paths
 */
export const PACKAGE_ROUTES = {
  LIST: "/packages",
  CREATE: "/packages/create",
  EDIT: "/packages/edit/:id",
  DETAILS: "/packages/:id",
  IMPORT: "/packages/import",
  IMPORTED: "/packages/imported",
  TRACK: "/track",
  LIVREUR_DASHBOARD: "/livreur/dashboard",
};

// ============================================================================
// PLACEHOLDERS
// ============================================================================

/**
 * Placeholder text for package form fields
 */
export const PACKAGE_PLACEHOLDERS = {
  TRACKING_NUMBER: "packages.enterTrackingNumber",
  CUSTOMER_NAME: "Enter customer name",
  CUSTOMER_PHONE: "0600000000",
  NOTE: "packages.form.notePlaceholder",
  DELIVERY_NOTE: "packages.deliveryNotePlaceholder",
};

// ============================================================================
// VALIDATION RULES
// ============================================================================

/**
 * Package validation constants
 */
export const PACKAGE_VALIDATION = {
  MIN_QUANTITY: 1,
  MIN_PRICE: 0,
  MIN_PRODUCTS: 1,
  PHONE_MIN_LENGTH: 10,
  PHONE_MAX_LENGTH: 10,
};

// ============================================================================
// EXPORT CONFIGURATION
// ============================================================================

/**
 * Package export file configuration
 */
export const PACKAGE_EXPORT_CONFIG = {
  FILE_NAME_PREFIX: "packages",
  DATE_FORMAT: "yyyy-MM-dd",
  SHEET_NAME: "Packages",
};

// ============================================================================
// PAGINATION & LIMITS
// ============================================================================

/**
 * Package list pagination settings
 */
export const PACKAGE_PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
};

// ============================================================================
// QUERY KEYS
// ============================================================================

/**
 * React Query keys for package-related queries
 */
export const PACKAGE_QUERY_KEYS = {
  BASE: "packages",
  DETAIL: "package",
  LIVREUR: "livreur-packages",
  LIVREUR_DASHBOARD: "livreur-dashboard",
  IMPORTED: "imported-packages",
  TRACKING: "package-tracking",
};
